import type { IEnrichedModelInfo, IModelDefaults } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import {
  Alert,
  Badge,
  Button,
  Collapse,
  type ComboboxData,
  type ComboboxItemGroup,
  Divider,
  Group,
  Paper,
  Select,
  type SelectProps,
  Stack,
  Switch,
  Text,
} from "@mantine/core"
import { IconChevronDown, IconLock } from "@tabler/icons-react"
import { useMemo, useState } from "react"
import {
  CAPABILITY_LABEL,
  humanizeId,
  INHERIT_VALUE,
  PROVIDER_LABEL,
  TASK_COPY,
  TIER_COPY,
  taskModelKey,
} from "../../model-tiers"
import type { IProps } from "./interfaces"

type TTierDefault = IModelDefaults["tiers"][number]
type TTaskDefault = IModelDefaults["tasks"][number]

const REPORTS_GATE_KEY = "reports_best_model_enabled"

/** Tidy a credit ratio for display: at most one decimal, no trailing `.0`. */
function formatMultiplier(n: number): string {
  const rounded = Math.round(n * 10) / 10
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1)
}

interface IModelSelectProps {
  models: IEnrichedModelInfo[]
  /** Raw setting value — "" means unset (falls back to the tier/built-in default). */
  value: string
  onChange: (raw: string) => void
  disabled: boolean
  /** Label for the "no explicit choice" option (Automatic for a tier, Inherit for a task). */
  inheritLabel: string
  ariaLabel: string
}

/**
 * A model picker grouped by provider. The first option is always the
 * "inherit/automatic" choice (clears the key); unpriced models list but render
 * disabled with a "not priced yet" note. The dropdown shows a friendly name plus
 * a plain capability + credit-multiplier line so a non-technical admin can read
 * the cost/quality tradeoff without knowing model IDs.
 */
function ModelSelect({
  models,
  value,
  onChange,
  disabled,
  inheritLabel,
  ariaLabel,
}: IModelSelectProps): React.JSX.Element {
  const { i18n } = useLingui()

  const modelById = useMemo(() => new Map(models.map((m) => [m.id, m] as const)), [models])

  const data = useMemo<ComboboxData>(() => {
    const groups: ComboboxItemGroup[] = []
    for (const provider of ["anthropic", "openai", "openrouter"] as const) {
      const items = models
        .filter((m) => m.provider === provider)
        .map((m) => ({ value: m.id, label: m.name, disabled: !m.rateCarded }))
      if (items.length > 0) groups.push({ group: PROVIDER_LABEL[provider], items })
    }
    const out: ComboboxData[number][] = [{ value: INHERIT_VALUE, label: inheritLabel }, ...groups]
    // Defensive: a saved model whose provider key was later removed isn't in the
    // list — add it so the input still shows what's configured.
    if (value.length > 0 && !modelById.has(value)) out.push({ value, label: value })
    return out
  }, [models, inheritLabel, value, modelById])

  const renderOption: SelectProps["renderOption"] = ({ option }) => {
    if (option.value === INHERIT_VALUE) return <Text size="sm">{option.label}</Text>
    const model = modelById.get(option.value)
    if (!model) return <Text size="sm">{option.label}</Text>
    const mult = formatMultiplier(model.costMultiplier)
    const detail = model.rateCarded
      ? `${i18n._(CAPABILITY_LABEL[model.capability])} · ${i18n._(msg`≈${mult}× credits`)}`
      : i18n._(CAPABILITY_LABEL.unknown)
    return (
      <div>
        <Text size="sm">{model.name}</Text>
        <Text size="xs" c="dimmed">
          {detail} · {model.id}
        </Text>
      </div>
    )
  }

  return (
    <Select
      data={data}
      value={value.length > 0 ? value : INHERIT_VALUE}
      onChange={(v) => onChange(v === INHERIT_VALUE || v === null ? "" : v)}
      renderOption={renderOption}
      disabled={disabled}
      allowDeselect={false}
      searchable
      comboboxProps={{ withinPortal: true }}
      aria-label={ariaLabel}
      maxDropdownHeight={320}
    />
  )
}

/** The persistent capability + credit caption for the model currently selected. */
function SelectedMeta({ model }: { model: IEnrichedModelInfo | undefined }): React.JSX.Element | null {
  const { i18n } = useLingui()
  if (!model?.rateCarded) return null
  const mult = formatMultiplier(model.costMultiplier)
  return (
    <Text size="xs" c="dimmed" mt={4}>
      {i18n._(CAPABILITY_LABEL[model.capability])} · {i18n._(msg`≈${mult}× credits`)}
    </Text>
  )
}

/**
 * Dumb Model-selection view. Three tier pickers (Light / Default / Heavy) plus a
 * collapsible per-task override list, each with plain-language guidance on where a
 * cheaper model is safe and where it shows. Controlled: settings in, change +
 * save events out. The smart wrapper owns the model list and the role/mode props.
 */
export function ModelsPanelView({
  models,
  defaults,
  settings,
  saving,
  canWrite,
  isAdminOnly,
  updateSetting,
  save,
}: IProps): React.JSX.Element {
  const { i18n } = useLingui()
  const [overridesOpen, setOverridesOpen] = useState<boolean>(false)

  const modelById = useMemo(() => new Map(models.map((m) => [m.id, m] as const)), [models])

  // Every structural fact comes from the endpoint's `defaults`: the tier list +
  // order, each tier's configured model, and per-task tier/floor/effective model.
  const tierByName = useMemo(() => new Map(defaults.tiers.map((t) => [t.tier, t] as const)), [defaults])
  const tasksByTier = useMemo(() => {
    const grouped = new Map<string, TTaskDefault[]>()
    for (const task of defaults.tasks) {
      const list = grouped.get(task.tier) ?? []
      list.push(task)
      grouped.set(task.tier, list)
    }
    return grouped
  }, [defaults])

  const modelName = (id: string): string => modelById.get(id)?.name ?? id
  const tierLabel = (tier: string): string => {
    const copy = TIER_COPY[tier]
    return copy ? i18n._(copy.label) : humanizeId(tier)
  }

  // Every key this panel writes, derived from the endpoint (no hardcoded key list):
  // the tier defaults + one per-task override each, plus the report gate in admin mode.
  const modelSettingKeys = [
    ...defaults.tiers.map((t) => t.settingKey),
    ...defaults.tasks.map((t) => taskModelKey(t.action)),
  ]
  const saveKeys = isAdminOnly ? [...modelSettingKeys, REPORTS_GATE_KEY] : modelSettingKeys
  const reportsGateEnabled = settings[REPORTS_GATE_KEY] !== "false"

  // The "Automatic" label for a tier picker: name the tier's recommendation
  // (served by the backend resolver — never hardcoded here), or say "per task"
  // when the tier's task floors diverge (Light: Haiku for most, Sonnet for
  // chapter detection). Tiers without tasks (Heavy — reached only via report
  // upgrades) still name their floor from defaults.tiers.
  const tierAutomaticLabel = (tier: IModelDefaults["tiers"][number]): string => {
    const floorNames = [...new Set((tasksByTier.get(tier.tier) ?? []).map((t) => t.floorModelName))]
    if (floorNames.length > 1) return i18n._(msg`Automatic — recommended per task (see table)`)
    const name = floorNames[0] ?? tier.floorModelName
    return i18n._(msg`Automatic — ${name} (recommended)`)
  }

  // The "Inherit" label for a task: what running it inherits (and from where).
  // With no override the endpoint's effectiveModel/source ARE the inherited truth
  // (used verbatim); with an override active, show what it WOULD fall back to
  // (tier setting → else the hardcoded floor).
  const taskInheritLabel = (task: TTaskDefault): string => {
    const hasOverride = (settings[taskModelKey(task.action)] ?? "").length > 0
    const tierName = tierLabel(task.tier)
    if (!hasOverride) {
      const name = task.effectiveModelName
      if (task.source === "tier-setting") return i18n._(msg`Inherit — ${name} (from the ${tierName} tier)`)
      return i18n._(msg`Inherit — ${name} (recommended default)`)
    }
    const tierConfigured = tierByName.get(task.tier)?.configuredModel ?? null
    if (tierConfigured) {
      const name = modelName(tierConfigured)
      return i18n._(msg`Inherit — ${name} (from the ${tierName} tier)`)
    }
    const floor = task.floorModelName
    return i18n._(msg`Inherit — ${floor} (recommended default)`)
  }

  const tiersWithTasks = defaults.tiers.filter((tier) => (tasksByTier.get(tier.tier) ?? []).length > 0)

  return (
    <Stack gap="md">
      <Alert variant="light" color="info">
        <Text size="sm">
          <Trans>
            AI work runs in three tiers. Light handles high-volume background steps, Default writes what you read every
            day, and Heavy is the premium model for one-off report upgrades. Pick a model per tier, or fine-tune an
            individual task below. Cheaper models use fewer credits.
          </Trans>
        </Text>
      </Alert>

      {!canWrite && (
        <Alert variant="light" color="warning" icon={<IconLock size={16} />}>
          <Text size="sm">
            <Trans>You can see which models this workspace uses, but only an owner or admin can change them.</Trans>
          </Text>
        </Alert>
      )}

      {defaults.tiers.map((tier: TTierDefault) => {
        const key = tier.settingKey
        const value = settings[key] ?? ""
        const copy = TIER_COPY[tier.tier]
        const heading = copy ? i18n._(copy.label) : humanizeId(tier.tier)
        const isHeavy = tier.tier === "heavy"
        return (
          <Paper withBorder p="md" key={tier.tier}>
            <Text fw={600} mb={2}>
              {heading}
            </Text>
            {copy && (
              <Text size="xs" c="dimmed" mb="sm">
                {i18n._(copy.consequence)}
              </Text>
            )}
            <ModelSelect
              models={models}
              value={value}
              onChange={(raw) => updateSetting(key, raw)}
              disabled={!canWrite}
              inheritLabel={tierAutomaticLabel(tier)}
              ariaLabel={heading}
            />
            <SelectedMeta model={modelById.get(value.length > 0 ? value : (tier.configuredModel ?? tier.floorModel))} />
            {(() => {
              const effectiveId = value.length > 0 ? value : (tier.configuredModel ?? tier.floorModel)
              const effective = modelById.get(effectiveId)
              if (effectiveId && !effective?.rateCarded) {
                return (
                  <Alert color="warning" variant="light" mt="sm">
                    <Text size="sm">
                      <Trans>
                        This model is no longer available. Pick another Default / Light / Heavy model so new work does
                        not fail.
                      </Trans>
                    </Text>
                  </Alert>
                )
              }
              return null
            })()}

            {/* The heavy-model gate lives with the tier it controls. Platform admins
                (super-admin view) set the platform default here — they have no
                Report Types card. Company admins turn it on per-workspace under
                Reports & output → Report Types, so they only get a pointer. */}
            {isHeavy && isAdminOnly && (
              <>
                <Divider my="sm" />
                <Group justify="space-between" wrap="nowrap" align="flex-start" gap="sm">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" fw={600}>
                      <Trans>Allow the heavy model for reports</Trans>
                    </Text>
                    <Text size="xs" c="dimmed">
                      <Trans>
                        Let people spend extra credits to generate a single report on the Heavy model — sharper
                        synthesis and richer diagrams. Reports always default to the standard model; this only offers
                        the upgrade as a choice. Companies can turn it off for their own workspace.
                      </Trans>
                    </Text>
                  </div>
                  <Switch
                    checked={reportsGateEnabled}
                    onChange={(e) => updateSetting(REPORTS_GATE_KEY, e.currentTarget.checked ? "true" : "false")}
                    disabled={!canWrite}
                    aria-label={i18n._(msg`Allow the heavy model for reports`)}
                    mt={4}
                  />
                </Group>
              </>
            )}
            {isHeavy && !isAdminOnly && (
              <Text size="xs" c="dimmed" mt="sm">
                <Trans>
                  Reports upgrade to this model only when someone opts in. Turn that on for your workspace under Reports
                  & output → Report types.
                </Trans>
              </Text>
            )}
          </Paper>
        )
      })}

      <Paper withBorder p="md">
        <Group
          justify="space-between"
          wrap="nowrap"
          onClick={() => setOverridesOpen((o) => !o)}
          style={{ cursor: "pointer" }}
        >
          <div>
            <Text fw={600}>
              <Trans>Per-task overrides</Trans>
            </Text>
            <Text size="xs" c="dimmed">
              <Trans>Pin a specific model for one step. Leave a task on Inherit to follow its tier above.</Trans>
            </Text>
          </div>
          <IconChevronDown
            size={18}
            style={{ transform: overridesOpen ? "rotate(180deg)" : undefined, transition: "transform 150ms ease" }}
          />
        </Group>

        <Collapse in={overridesOpen}>
          <Stack gap="lg" mt="md">
            {tiersWithTasks.map((tier) => (
              <Stack gap="sm" key={tier.tier}>
                <Group gap="xs">
                  <Text size="xs" fw={700} tt="uppercase" c="dimmed">
                    {tierLabel(tier.tier)}
                  </Text>
                  <Badge size="xs" variant="light" color="gray">
                    <Trans>tier default</Trans>
                  </Badge>
                </Group>
                {(tasksByTier.get(tier.tier) ?? []).map((task) => {
                  const key = taskModelKey(task.action)
                  const value = settings[key] ?? ""
                  const copy = TASK_COPY[task.action]
                  const label = copy ? i18n._(copy.label) : humanizeId(task.action)
                  // Show capability + credits for what actually runs — the override
                  // if pinned, otherwise the inherited effective model.
                  const shownModel = modelById.get(value.length > 0 ? value : task.effectiveModel)
                  return (
                    <Group key={task.action} justify="space-between" align="flex-start" wrap="wrap" gap="sm">
                      <Stack gap={2} style={{ flex: 1, minWidth: 220 }}>
                        <Text size="sm" fw={600}>
                          {label}
                        </Text>
                        {copy && (
                          <Text size="xs" c="dimmed">
                            {i18n._(copy.description)}
                          </Text>
                        )}
                        {copy && (
                          <Text size="xs" c="dimmed" fs="italic">
                            {i18n._(copy.sensitivity)}
                          </Text>
                        )}
                      </Stack>
                      <div style={{ width: 260, maxWidth: "100%" }}>
                        <ModelSelect
                          models={models}
                          value={value}
                          onChange={(raw) => updateSetting(key, raw)}
                          disabled={!canWrite}
                          inheritLabel={taskInheritLabel(task)}
                          ariaLabel={label}
                        />
                        <SelectedMeta model={shownModel} />
                      </div>
                    </Group>
                  )
                })}
              </Stack>
            ))}
          </Stack>
        </Collapse>
      </Paper>

      {canWrite && (
        <Group>
          <Button onClick={() => save(saveKeys)} loading={saving}>
            <Trans>Save model selection</Trans>
          </Button>
        </Group>
      )}
    </Stack>
  )
}
